import { PlayerEvent, PlayerEventInterface } from '@cbsinteractive/avia-js';
import { VideoPlayerInterface } from '@cbsinteractive/avia-js/dist/types/';
import { ErrorInfoInterface } from '@cbsinteractive/avia-js/dist/types/iface/ErrorInfoInterface';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { IPlayerAdapterAdBreakMetadata } from 'library/ts/player/adapter/interfaces/events/detail/IPlayerAdapterAdBreakMetadata';
import { IPlayerAdapterAdStart } from 'library/ts/player/adapter/interfaces/events/detail/IPlayerAdapterAdStart';
import { IPlayerAdapterEventVO } from 'library/ts/player/adapter/interfaces/events/IPlayerAdapterEventVO';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { PlutoAdClickResolver } from 'tag/ts/tag/ads/pluto/PlutoAdClickResolver';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { IPlayerEventVO } from 'tag/ts/tag/interfaces/vtg/player/IPlayerEventVO';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { AviaPlayerWrapper } from 'tag/ts/tag/player/adapters/avia/AviaPlayerWrapper';
import { IAviaPlayerEventTransformer } from 'tag/ts/tag/player/adapters/avia/interfaces/adapter/IAviaPlayerEventTransformer';
import { IAviaEventTransformerArgument } from 'tag/ts/tag/player/adapters/avia/interfaces/arguments/IAviaEventTransformerArgument';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description VTG Avia player event transformer and Smart Tag integration bridge
 *
 * **Event System Integration Overview:**
 * This transformer serves as the critical bridge between VTG's Avia player event system and
 * Smart Tag's business logic architecture. It translates VTG's native player events into
 * Smart Tag's standardized event format, enabling consistent event handling across all
 * Smart Tag components regardless of underlying VTG implementation changes.
 *
 * **Event Translation Architecture:**
 *
 * **1. Comprehensive Event Mapping (50+ Event Types):**
 * - **Playback Events**: CONTENT_PLAYING, CONTENT_PAUSED, CONTENT_COMPLETE, PLAYBACK_STATE_CHANGE
 * - **Ad Events**: AD_START, AD_COMPLETE, AD_BREAK_START, AD_SKIPPED, AD_CUEPOINTS_AVAILABLE, SQUEEZEBACK_START, SQUEEZEBACK_END
 * - **Quality Events**: QUALITIES_CHANGE, QUALITY_CHANGE, CDN_CHANGE, PRESENTATION_SIZE_CHANGE
 * - **Error Events**: ERROR with detailed error classification and recovery context
 * - **DRM Events**: DRM_KEY_SYSTEM_CREATED for content protection workflows
 * - **Live Events**: LIVE_PRESENTATION_STOPPED, CONTENT_IS_PLAYING_LIVE for streaming
 * - **User Events**: FULLSCREEN_CHANGE, VOLUME_CHANGE, MUTE_CHANGE, SEEKING_CHANGE
 * - **Accessibility Events**: ACTION_KEY, ACTION_KEY_METADATA for accessibility hotkeys and actions
 *
 * **2. Event Enrichment Strategy:**
 * - **Smart Tag Context**: Adds Tag instance, player references, and session metadata
 * - **Business Metadata**: Includes resource config, player number, and embed context
 * - **Timing Information**: Preserves VTG timing while adding Smart Tag session context
 * - **Cross-Component Data**: Provides data needed by Model, View, Controller, and external apps
 *
 * **3. Integration Benefits:**
 * - **VTG Independence**: Smart Tag components unaffected by VTG event system changes
 * - **Business Logic Integration**: Events include Smart Tag business context and metadata
 * - **Multi-Target Dispatch**: Single event source feeds Tag components and external applications
 * - **Debug Support**: Event data aids troubleshooting across integration layers
 *
 * **Event Flow Architecture:**
 * ```
 * VTG Avia Player → AviaEventTransformer → Smart Tag Event System →
 * [Tag Components + External Applications + Skin UI]
 * ```
 *
 * **Business Impact:**
 * Enables Smart Tag to maintain its business logic and component architecture while
 * leveraging VTG's video player technology, maintaining both technical functionality
 * and business flexibility for the streaming platform.
 */
export class AviaEventTransformer extends AbstractTagClass implements IAviaPlayerEventTransformer {

	// reference to AviaPlayer class instance
	#aviaPlayerWrapper: AviaPlayerWrapper;
	// placeholder for Tag event VO class instance
	#tagEventVO: TagEventVO;
	#plutoAdClickResolver: PlutoAdClickResolver = new PlutoAdClickResolver();

	// list of trackable avia events mapped to player adapter internal events
	#trackableEvents: { [ key: string ]: PlayerAdapterEvent; } = {
		[ PlayerEvent.ACTION_KEY ]: PlayerAdapterEvent.ACTION_KEY,
		[ PlayerEvent.ACTION_KEY_METADATA ]: PlayerAdapterEvent.ACTION_KEY_METADATA,
		[ PlayerEvent.ACTIVE_STATE_CHANGE ]: PlayerAdapterEvent.ACTIVE_STATE_CHANGE,
		[ PlayerEvent.AD_BREAK_COMPLETE ]: PlayerAdapterEvent.AD_BREAK_COMPLETE,
		[ PlayerEvent.AD_BREAK_DISCARDED ]: PlayerAdapterEvent.AD_BREAK_DISCARDED,
		[ PlayerEvent.AD_BREAK_METADATA ]: PlayerAdapterEvent.AD_BREAK_METADATA,
		[ PlayerEvent.AD_BREAK_START ]: PlayerAdapterEvent.AD_BREAK_START,
		[ PlayerEvent.AD_CLICK ]: PlayerAdapterEvent.AD_CLICK,
		[ PlayerEvent.AD_COMPLETE ]: PlayerAdapterEvent.AD_COMPLETE,
		[ PlayerEvent.AD_CUEPOINTS_AVAILABLE ]: PlayerAdapterEvent.AD_CUEPOINTS_AVAILABLE,
		[ PlayerEvent.AD_FIRST_QUARTILE ]: PlayerAdapterEvent.AD_FIRST_QUARTILE,
		[ PlayerEvent.AD_MIDPOINT ]: PlayerAdapterEvent.AD_MIDPOINT,
		[ PlayerEvent.AD_PAUSED ]: PlayerAdapterEvent.AD_PAUSED,
		[ PlayerEvent.AD_PLAYING ]: PlayerAdapterEvent.AD_PLAYING,
		[ PlayerEvent.AD_PROGRESS ]: PlayerAdapterEvent.AD_PROGRESS,
		[ PlayerEvent.AD_SEGMENT_END ]: PlayerAdapterEvent.AD_SEGMENT_END,
		[ PlayerEvent.AD_SEGMENT_REENTERED ]: PlayerAdapterEvent.AD_SEGMENT_REENTERED,
		[ PlayerEvent.AD_SEGMENT_START ]: PlayerAdapterEvent.AD_SEGMENT_START,
		[ PlayerEvent.AD_SKIPPABLE_STATE_CHANGE ]: PlayerAdapterEvent.AD_SKIPPABLE_STATE_CHANGE,
		[ PlayerEvent.AD_SKIPPED ]: PlayerAdapterEvent.AD_SKIPPED,
		[ PlayerEvent.AD_STALLED ]: PlayerAdapterEvent.AD_STALLED,
		[ PlayerEvent.AD_START ]: PlayerAdapterEvent.AD_START,
		[ PlayerEvent.AD_THIRD_QUARTILE ]: PlayerAdapterEvent.AD_THIRD_QUARTILE,
		[ PlayerEvent.AUDIO_TRACK_CHANGE ]: PlayerAdapterEvent.AUDIO_TRACK_CHANGE,
		[ PlayerEvent.AUDIO_TRACKS_CHANGE ]: PlayerAdapterEvent.AUDIO_TRACKS_CHANGE,
		[ PlayerEvent.AUTOPLAY_BLOCKED ]: PlayerAdapterEvent.AUTOPLAY_BLOCKED,
		[ PlayerEvent.CDN_CHANGE ]: PlayerAdapterEvent.CDN_CHANGE,
		[ PlayerEvent.CLOCK ]: PlayerAdapterEvent.CLOCK,
		[ PlayerEvent.CONTENT_COMPLETE ]: PlayerAdapterEvent.CONTENT_COMPLETE,
		[ PlayerEvent.CONTENT_DURATION_AVAILABLE ]: PlayerAdapterEvent.CONTENT_DURATION_AVAILABLE,
		[ PlayerEvent.CONTENT_IS_PLAYING_LIVE ]: PlayerAdapterEvent.CONTENT_IS_PLAYING_LIVE,
		[ PlayerEvent.CONTENT_PAUSED ]: PlayerAdapterEvent.CONTENT_PAUSED,
		[ PlayerEvent.CONTENT_PLAYING ]: PlayerAdapterEvent.CONTENT_PLAYING,
		[ PlayerEvent.CONTENT_PROGRESS ]: PlayerAdapterEvent.CONTENT_PROGRESS,
		[ PlayerEvent.CONTENT_SEGMENT_END ]: PlayerAdapterEvent.CONTENT_SEGMENT_END,
		[ PlayerEvent.CONTENT_SEGMENT_START ]: PlayerAdapterEvent.CONTENT_SEGMENT_START,
		[ PlayerEvent.CONTENT_START ]: PlayerAdapterEvent.CONTENT_START,
		[ PlayerEvent.DRM_KEY_SYSTEM_CREATED ]: PlayerAdapterEvent.DRM_KEY_SYSTEM_CREATED,
		[ PlayerEvent.ERROR ]: PlayerAdapterEvent.ERROR,
		[ PlayerEvent.FULLSCREEN_CHANGE ]: PlayerAdapterEvent.FULLSCREEN_CHANGE,
		[ PlayerEvent.LANGUAGE_CHANGE ]: PlayerAdapterEvent.LANGUAGE_CHANGE,
		[ PlayerEvent.LIVE_PRESENTATION_STOPPED ]: PlayerAdapterEvent.LIVE_PRESENTATION_STOPPED,
		[ PlayerEvent.LOG_EVENT ]: PlayerAdapterEvent.LOG_EVENT,
		[ PlayerEvent.METADATA_CUEPOINT ]: PlayerAdapterEvent.METADATA_CUEPOINT,
		[ PlayerEvent.METRIC ]: PlayerAdapterEvent.METRIC,
		[ PlayerEvent.MUTE_CHANGE ]: PlayerAdapterEvent.MUTE_CHANGE,
		[ PlayerEvent.PLAYBACK_RESUMED ]: PlayerAdapterEvent.PLAYBACK_RESUMED,
		[ PlayerEvent.PLAYBACK_STARTED ]: PlayerAdapterEvent.PLAYBACK_STARTED,
		[ PlayerEvent.PLAYBACK_STATE_CHANGE ]: PlayerAdapterEvent.PLAYBACK_STATE_CHANGE,
		[ PlayerEvent.PLAYBACK_SUSPENDED ]: PlayerAdapterEvent.PLAYBACK_SUSPENDED,
		[ PlayerEvent.PLUGIN_EVENT ]: PlayerAdapterEvent.PLUGIN_EVENT,
		[ PlayerEvent.PRESENTATION_SIZE_CHANGE ]: PlayerAdapterEvent.PRESENTATION_SIZE_CHANGE,
		[ PlayerEvent.QUALITIES_CHANGE ]: PlayerAdapterEvent.QUALITIES_CHANGE,
		[ PlayerEvent.QUALITY_CHANGE ]: PlayerAdapterEvent.QUALITY_CHANGE,
		[ PlayerEvent.READY ]: PlayerAdapterEvent.READY,
		[ PlayerEvent.RESOURCE_BUFFERING ]: PlayerAdapterEvent.RESOURCE_BUFFERING,
		[ PlayerEvent.RESOURCE_END ]: PlayerAdapterEvent.RESOURCE_END,
		[ PlayerEvent.RESOURCE_START ]: PlayerAdapterEvent.RESOURCE_START,
		[ PlayerEvent.SEEK_COMPLETE ]: PlayerAdapterEvent.SEEK_COMPLETE,
		[ PlayerEvent.SEEK_REDIRECT_COMPLETE ]: PlayerAdapterEvent.SEEK_REDIRECT_COMPLETE,
		[ PlayerEvent.SEEK_REDIRECT_START ]: PlayerAdapterEvent.SEEK_REDIRECT_START,
		[ PlayerEvent.SEEK_START ]: PlayerAdapterEvent.SEEK_START,
		[ PlayerEvent.STREAM_ID_AVAILABLE ]: PlayerAdapterEvent.STREAM_ID_AVAILABLE,
		[ PlayerEvent.STREAM_METADATA ]: PlayerAdapterEvent.STREAM_METADATA,
		[ PlayerEvent.STREAM_TYPE_CHANGE ]: PlayerAdapterEvent.STREAM_TYPE_CHANGE,
		[ PlayerEvent.TEXT_CUEPOINT ]: PlayerAdapterEvent.TEXT_CUEPOINT,
		[ PlayerEvent.TEXT_TRACK_AVAILABLE ]: PlayerAdapterEvent.TEXT_TRACK_AVAILABLE,
		[ PlayerEvent.TEXT_TRACK_CHANGE ]: PlayerAdapterEvent.TEXT_TRACK_CHANGE,
		[ PlayerEvent.TEXT_TRACK_ENABLED_CHANGE ]: PlayerAdapterEvent.TEXT_TRACK_ENABLED_CHANGE,
		[ PlayerEvent.TEXT_TRACKS_CHANGE ]: PlayerAdapterEvent.TEXT_TRACKS_CHANGE,
		[ PlayerEvent.THUMBNAIL_TRACK_AVAILABLE ]: PlayerAdapterEvent.THUMBNAIL_TRACK_AVAILABLE,
		[ PlayerEvent.VIDEO_ELEMENT_ADDED ]: PlayerAdapterEvent.VIDEO_ELEMENT_ADDED,
		[ PlayerEvent.VIDEO_ELEMENT_REMOVED ]: PlayerAdapterEvent.VIDEO_ELEMENT_REMOVED,
		[ PlayerEvent.VOLUME_CHANGE ]: PlayerAdapterEvent.VOLUME_CHANGE,
		[ PlayerEvent.WAITING_CHANGE ]: PlayerAdapterEvent.WAITING_CHANGE,
		[ PlayerEvent.SHOW_AD_UI ]: PlayerAdapterEvent.SHOW_AD_UI,
		[ PlayerEvent.HIDE_AD_UI ]: PlayerAdapterEvent.HIDE_AD_UI,
		[ PlayerEvent.SQUEEZEBACK_START ]: PlayerAdapterEvent.SQUEEZEBACK_START,
		[ PlayerEvent.SQUEEZEBACK_END ]: PlayerAdapterEvent.SQUEEZEBACK_END
	};

	constructor( argument: IAviaEventTransformerArgument ) {
		super(
			{
				...argument,
				name: 'AviaEventTransformer'
			}
		);
		// store instance of AviaPlayer class for later use
		this.#aviaPlayerWrapper = argument?.aviaPlayerWrapper;
	}

	/**
	 * @description Returns instance of adapted player API
	 * @returns {IAviaPlayerAdapterAPI}
	 */
	get player(): VideoPlayerInterface {
		return this.#aviaPlayerWrapper?.adaptedPlayerInstance;
	}

	/**
	 * @description Transforms VTG Avia player events into Smart Tag's enriched event architecture
	 *
	 * **Event Transformation Strategy:**
	 * This method implements the core VTG-to-Smart Tag event translation that enables Smart Tag
	 * to maintain its business logic architecture while leveraging VTG's video player technology.
	 * The transformation enriches VTG events with Smart Tag business context and metadata.
	 *
	 * **Transformation Process:**
	 *
	 * **1. Smart Tag Event Structure Creation:**
	 * - Creates TagEventVO with ON_PLAYER_EVENT type for Smart Tag event system
	 * - Establishes event hierarchy: Tag → Player → Component → External App
	 * - Provides consistent event structure regardless of VTG implementation changes
	 *
	 * **2. VTG Event Type Mapping:**
	 * - Maps VTG PlayerEvent types to Smart Tag PlayerAdapterEvent types
	 * - Ensures 1:1 correspondence between VTG and Smart Tag event semantics
	 * - Handles 50+ event types covering playback, ads, quality, errors, DRM, and user interactions
	 *
	 * **3. Business Context Enrichment:**
	 * - **Player References**: Adds Smart Tag player API and adapted VTG player instance
	 * - **Session Metadata**: Includes player number, resource config, and embed context
	 * - **Component Integration**: Provides data needed by Model, View, Controller components
	 * - **External App Data**: Includes references for parent application event handling
	 *
	 * **4. Platform-Specific Optimization:**
	 * - **Desktop**: Deep merges VTG event for complete data preservation and debugging
	 * - **Smart TV/HTML5**: Uses reference passing for memory efficiency on constrained devices
	 * - **Cross-Platform**: Maintains consistent Smart Tag API regardless of platform
	 *
	 * **5. Event Target Substitution:**
	 * - Replaces VTG event target with Smart Tag player adapter for API consistency
	 * - Ensures external applications interact with Smart Tag APIs, not VTG directly
	 * - Maintains Smart Tag business logic control over player interactions
	 *
	 * **Integration Benefits:**
	 * - **VTG Abstraction**: Smart Tag components never directly handle VTG events
	 * - **Business Logic Preservation**: Events include Smart Tag business context
	 * - **Debug Enhancement**: Rich metadata aids troubleshooting across integration layers
	 * - **API Consistency**: Unified event interface regardless of underlying VTG changes
	 *
	 * @param {PlayerEventInterface} event Native VTG Avia player event to transform
	 * @returns {TagEventVO} Smart Tag event enriched with business context and integration metadata
	 */
	#getInternalEvent = ( event: PlayerEventInterface ): TagEventVO => {
		// create Tag event VO object that we are going to dispatch with player events
		this.#tagEventVO = this.getTagEventVO(
			TagEvent.ON_PLAYER_EVENT,
			this.tag,
			this.getTagEventDataVO(),
			this,
			this.name
		);

		// reset data property to empty object
		this.#tagEventVO.data = {};

		// original avia player event cast to our internal interface
		const aviaPlayerEvent = event as IPlayerEventVO;

		// video player adapter event (internal)
		const internalEvent = this.#trackableEvents[ event?.type ];

		if ( !this.utils.Type.isEmpty( internalEvent ) ) {
			// store iPlayerAPI instance to dispatch with the event
			this.#tagEventVO.data.player = this.#aviaPlayerWrapper.API;
			// store avia instance to dispatch with the event
			this.#tagEventVO.data.adaptedPlayerInstance = this.player;
			// number of player instance created within current session
			this.#tagEventVO.data.playerNumber = this.tag.controller.playerManager.playerAdapter.playerNumber;
			// current resource config
			this.#tagEventVO.data.resourceConfig = this.tag.controller.playerManager.currentResource;
			// store original avia player event
			this.#tagEventVO.data.adaptedPlayerEvent = aviaPlayerEvent;

			if ( this?.params?.SYSTEM?.embedType === EmbedType.DESKTOP ) {
				// merge original avia event into player adapter event property
				this.#tagEventVO.data.playerAdapterEvent = this.utils.DeepMerge.deepmerge( {}, aviaPlayerEvent );
			} else {
				// for all other embed types do not deep merge avia event object and pass it by reference
				this.#tagEventVO.data.playerAdapterEvent = aviaPlayerEvent as IPlayerAdapterEventVO;
			}

			// replace reference to original avia event target with internal player API
			this.#tagEventVO.data.playerAdapterEvent.target = this.tag.controller.playerManager.playerAdapter.player;
			// replace avia event type with our internal event type
			this.#tagEventVO.data.playerAdapterEvent.type = internalEvent;

			this.#processPlutoAdClickData( internalEvent, this.#tagEventVO.data.playerAdapterEvent );

			// IMPORTANT: Smart Tag player adapter event object for external consumption
			this.#tagEventVO.data.playerEvent = this.#tagEventVO.data.playerAdapterEvent;
		}

		return this.#tagEventVO;
	};

	#processPlutoAdClickData = ( internalEvent: PlayerAdapterEvent, playerAdapterEvent: IPlayerAdapterEventVO ): void => {
		switch ( internalEvent ) {
			case PlayerAdapterEvent.AD_BREAK_METADATA:
				if ( this.#canResolvePlutoAdClickData() === false ) {
					return;
				}

				this.#plutoAdClickResolver.cacheAdBreakInfo( playerAdapterEvent?.detail as IPlayerAdapterAdBreakMetadata );
				break;

			case PlayerAdapterEvent.AD_START:
				if ( this.#canResolvePlutoAdClickData() === false ) {
					return;
				}

				this.#applyPlutoAdClickData( playerAdapterEvent );
				break;
		}
	};

	#applyPlutoAdClickData = ( playerAdapterEvent: IPlayerAdapterEventVO ): void => {
		const detail = playerAdapterEvent?.detail as IPlayerAdapterAdStart;
		const clickData = this.#plutoAdClickResolver.resolve( this.tag?.snapshot?.stitcher?.session, detail?.adInfo );

		if ( !clickData ) {
			return;
		}

		// Tag-layer session enrichment is the source of truth; Skin validates again before opening URLs.
		detail.adInfo.clickThroughUrl = clickData.clickThrough;
		detail.adInfo.clickTrackingUrls = clickData.clickTrackingUrls;
	};

	#canResolvePlutoAdClickData = (): boolean => {
		return (
			this?.params?.SYSTEM?.embedType === EmbedType.DESKTOP
			&& this.hasStitcherParams()
			&& this.tag?.model?.adUtilities?.adSetupInfo()?.setup?.definition === AdSetupDefinition.STITCHER
		);
	};

	/**
	 * @description Orchestrates VTG event processing with sophisticated error handling and Smart Tag integration
	 *
	 * **Event Processing Architecture:**
	 * This method serves as the primary VTG event handler that coordinates event transformation,
	 * error handling, and Smart Tag component dispatch. It implements Paramount's resilient
	 * event processing strategy that ensures video service continuity even during error conditions.
	 *
	 * **Event Processing Pipeline:**
	 *
	 * **1. Event Transformation:**
	 * - Converts VTG native events to Smart Tag enriched events via `#getInternalEvent()`
	 * - Validates event mapping exists in Smart Tag's event taxonomy
	 * - Ensures consistent event structure for all Smart Tag components
	 *
	 * **2. Critical Error Handling (Fatal Error Recovery):**
	 * - **Error Detection**: Identifies fatal VTG player errors requiring recovery
	 * - **Offline Exception (PLBCK-8332)**: both steps below are skipped for a network-category
	 *   fatal while Smart Tag already knows the connection is offline, so the adapter survives
	 *   for a later recovery attempt instead of being torn down
	 * - **Event Cleanup**: Unsubscribes from VTG events to prevent cascade failures
	 * - **Player Recovery**: Triggers player stop request for clean error recovery
	 * - **Service Continuity**: Ensures Smart Tag can recover from VTG player failures
	 *
	 * **3. Event Filtering Strategy:**
	 * - **High-Frequency Events**: Filters CLOCK, LOG_EVENT to prevent event flooding
	 * - **Progress Events**: Suppresses verbose progress logging while preserving functionality
	 * - **Debug Events**: Logs meaningful events while filtering noise for debugging efficiency
	 * - **Unknown Events**: Warns about unmapped VTG events for integration maintenance
	 *
	 * **4. Smart Tag Component Integration:**
	 * - **PlayerManager First**: Routes events to PlayerManager for player state management
	 * - **Component Cascade**: PlayerManager then dispatches to Model, View, Controller
	 * - **External Apps**: Events flow to parent applications and Skin UI components
	 * - **Consistent Interface**: All components receive same enriched event structure
	 *
	 * **Error Recovery Strategy:**
	 * When VTG player encounters fatal errors:
	 * 1. **Immediate Cleanup**: Unsubscribe from VTG events to prevent cascade failures
	 * 2. **Graceful Recovery**: Request player stop for clean state reset
	 * 3. **Service Continuity**: Allow Smart Tag to reinitialize with new content
	 * 4. **Error Isolation**: Prevent VTG errors from corrupting Smart Tag state
	 *
	 * **Integration Benefits:**
	 * - **Error Isolation**: VTG errors contained and managed with recovery workflows
	 * - **Performance**: Event filtering prevents unnecessary processing overhead
	 * - **Debug Support**: Filtering reduces log noise while maintaining troubleshooting data
	 * - **Component Coordination**: Ensures all Smart Tag components receive consistent events
	 *
	 * **Business Impact:**
	 * Allows video service to maintain availability and user experience
	 * even when underlying VTG player technology encounters errors or issues.
	 *
	 * @param {PlayerEventInterface} event VTG Avia player event to process and dispatch
	 */
	onPlayerEvent = ( event: PlayerEventInterface ): void => {
		this.#tagEventVO = this.#getInternalEvent( event );

		// filter per avia event type and process them if needed BEFORE dispatching internally as PlayerAdapterEvent
		// Process ERROR event
		const errorDetail = event?.detail as ErrorInfoInterface;

		if (
			event?.type === PlayerEvent.ERROR
			&&
			errorDetail?.fatal === true
			&&
			this.utils.Type.isFunction( this.#aviaPlayerWrapper?.API?.removePlayerEventListener )
		) {
			// a network fatal while already known-offline doesn't end the session — skip unsubscribe
			// and player stop so the adapter stays alive for a later recovery attempt. Opt-in only
			// (PLBCK-8332): returns false unless the host set TAG.offlineFatalRecovery === true
			if ( this.tag?.tagUtilities?.isNetworkFatalWhileOffline( errorDetail ) ) {
				this?.logger?.log( 'onPlayerEvent', 'skipping unsubscribe/stop: network fatal while offline', errorDetail );
			} else {
				// unsubscribe from further avia events - if fails, still proceed with player stop
				this.#aviaPlayerWrapper.API.removePlayerEventListener().then(
					( data ) => {
						this?.logger?.log( 'Fatal error: Successfully unsubscribed from events' );
					}
				).catch(
					( error ) => {
						this?.logger?.warn( 'Fatal error: Failed to unsubscribe from events:', error );
					}
				).finally(
					() => {
						// Always request player stop - this is critical for error recovery regardless of unsubscription result
						this.tag.dispatchTagEvent(
							// request player stop
							this.tag.getTagEventVO(
								TagEvent.ON_TAG_PLAYER_STOP_REQUEST,
								this?.tag,
								undefined,
								this,
								this?.name
							)
						);
					}
				);
			}
		}

		// if a match for avia event was found in our internal events
		if ( !this.utils.Type.isEmpty( this.#tagEventVO?.data?.playerAdapterEvent?.type ) ) {
			switch ( this.#tagEventVO?.data?.playerAdapterEvent?.type ) {
				case PlayerAdapterEvent.CLOCK:
				case PlayerAdapterEvent.LOG_EVENT:
					// do not dispatch these events
					break;
				default:
					// filter our console log
					switch ( this.#tagEventVO?.data?.playerAdapterEvent?.type ) {
						case PlayerAdapterEvent.ACTIVE_STATE_CHANGE:
						case PlayerAdapterEvent.AD_PROGRESS:
						case PlayerAdapterEvent.CONTENT_PROGRESS:
						case PlayerAdapterEvent.METRIC:
							// do not log these events
							break;
						default:
							this?.logger?.log( 'onPlayerEvent', this.#tagEventVO.data.playerAdapterEvent.type, this.#tagEventVO );
					}
					// surface our player event to player manager before dispatching it to the rest of application via regular means
					this.tag.controller.playerManager.onPlayerAdapterEvent( this.#tagEventVO );
			}
		} else {
			// otherwise, warn about avia event that was not covered by our internal event (i.e. new avia event added to recent avia version)
			this?.logger?.warn( 'onPlayerEvent', 'Match for avia event:', event?.type, 'was not found or not internalized => do not dispatch!' );
		}
	};
}